import { AlertTriangle, CheckCircle2, DatabaseBackup, GitCommitHorizontal, History, RefreshCw, RotateCcw, ShieldCheck, Wrench } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  loadSystemHealth,
  runRecoveryAction,
  type HealthState,
  type OperationsStatus,
  type RecoveryAction,
  type SystemHealthSnapshot,
} from './system-health-api';
import './system-health.css';

function tone(state: HealthState): 'good' | 'warning' | 'bad' | 'neutral' {
  if (state === 'healthy') return 'good';
  if (state === 'offline') return 'bad';
  if (state === 'starting' || state === 'recovering' || state === 'degraded') return 'warning';
  return 'neutral';
}

function stateLabel(state: HealthState): string {
  if (state === 'healthy') return 'Healthy';
  if (state === 'starting') return 'Starting';
  if (state === 'recovering') return 'Recovering';
  if (state === 'degraded') return 'Degraded';
  if (state === 'offline') return 'Offline';
  if (state === 'disabled') return 'Disabled';
  return 'Unknown';
}

function friendlyToken(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase()) || 'Unknown';
}

function styleLabel(value: string): string {
  if (value === 'win98') return 'Win98';
  if (value === 'tfi-friday') return 'TFI Friday';
  if (value === 'black-yellow') return 'Black & Yellow';
  return friendlyToken(value);
}

function readinessLabel(value: SystemHealthSnapshot['streamSetup']['readiness'] | undefined): string {
  if (value === 'ready') return 'Ready';
  if (value === 'degraded-usable') return 'Usable with warnings';
  if (value === 'checking') return 'Checking';
  if (value === 'not-ready') return 'Not ready';
  return 'Unknown';
}

function shortCommit(value: string | null | undefined): string {
  return value ? value.slice(0, 8) : '—';
}

function formatStamp(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? date.toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })
    : value;
}

function deploymentLabel(operations: OperationsStatus): string {
  if (operations.deployment?.result === 'pass') return 'PASS';
  if (operations.deployment?.result === 'rollback') return 'ROLLED BACK';
  if (operations.deployment?.result === 'fail') return 'FAILED';
  return 'NO RECORD';
}

export function SystemHealthPage({ role }: { role: 'owner' | 'operator' }) {
  const [snapshot, setSnapshot] = useState<SystemHealthSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState('');
  const [message, setMessage] = useState('');
  const [incidentFilter, setIncidentFilter] = useState<'all' | 'open' | 'resolved'>('all');

  const refresh = useCallback(async () => {
    try {
      setSnapshot(await loadSystemHealth());
      setMessage('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'System Health could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), 10000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const systems = useMemo(() => {
    return (snapshot?.subsystems ?? []).slice().sort((left, right) => {
      if (left.required !== right.required) return left.required ? -1 : 1;
      const order: HealthState[] = ['offline', 'degraded', 'recovering', 'starting', 'unknown', 'healthy', 'disabled'];
      const stateOrder = order.indexOf(left.state) - order.indexOf(right.state);
      return stateOrder || left.label.localeCompare(right.label, 'en-GB');
    });
  }, [snapshot]);

  const modules = useMemo(
    () => (snapshot?.modules ?? []).slice().sort((left, right) => left.label.localeCompare(right.label, 'en-GB', { numeric: true })),
    [snapshot],
  );

  async function recover(action: RecoveryAction) {
    if (!window.confirm(`Run “${action.label}” for this system?`)) return;
    setBusyAction(action.id);
    setMessage('');
    try {
      await runRecoveryAction(action);
      await refresh();
      setMessage(`${action.label} requested.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Recovery action failed.');
    } finally {
      setBusyAction('');
    }
  }

  if (loading && !snapshot) return <div className="ffs-next-loading">Loading System Health…</div>;

  const attention = snapshot?.attention ?? [];
  const incidents = snapshot?.incidents ?? [];
  const visibleIncidents = incidents.filter((incident) =>
    incidentFilter === 'all'
      ? true
      : incidentFilter === 'resolved'
        ? Boolean(incident.resolved_at)
        : !incident.resolved_at,
  );
  const overallTone = tone(snapshot?.overall ?? 'unknown');
  const mode = snapshot?.streamSetup.modes.safeModeEnabled
    ? 'Safe Mode'
    : snapshot?.streamSetup.modes.rehearsalEnabled
      ? 'Rehearsal'
      : 'Live';
  const operations = snapshot?.operations ?? null;
  const operationsTone = snapshot?.operationsError || operations?.deployment?.result === 'fail' || operations?.backup?.lastAttemptResult === 'fail'
    ? 'bad'
    : operations?.recovery?.ready === true
      ? 'good'
      : 'warning';
  const operationsLabel = snapshot?.operationsError
    ? 'STATUS UNAVAILABLE'
    : !operations?.available
      ? 'AWAITING HOST STATUS'
      : operations.deployment?.result === 'fail' || operations.backup?.lastAttemptResult === 'fail'
        ? 'ATTENTION REQUIRED'
        : operations.recovery?.ready === true
          ? 'RECOVERY READY'
          : 'RECOVERY UNVERIFIED';

  return (
    <div className="ffs-workspace-page ffs-health-page">
      {message ? <div className="ffs-workspace-message">{message}</div> : null}

      <section className="ffs-workspace-hero">
        <div><small>OPERATIONS</small><h2>System Health</h2><p>Problems and recovery actions first; healthy systems stay quiet and compact.</p></div>
        <div className="ffs-health-hero-actions"><span className={`ffs-status ${overallTone}`}>{stateLabel(snapshot?.overall ?? 'unknown').toUpperCase()}</span><button type="button" className="ffs-button secondary" onClick={() => void refresh()} disabled={Boolean(busyAction)}><RefreshCw size={15} /> Refresh</button></div>
      </section>

      <section className={`ffs-workspace-card ffs-health-attention ${attention.length ? 'has-items' : 'clear'}`}>
        <header><div><small>WHAT NEEDS ME</small><h3>Needs attention</h3></div>{attention.length ? <AlertTriangle size={19} /> : <CheckCircle2 size={19} />}</header>
        {attention.length === 0 ? <div className="ffs-health-clear"><CheckCircle2 size={21} /><div><strong>No obvious operational problems</strong><span>FFSControl is not currently reporting a subsystem that needs operator action.</span></div></div> : <div className="ffs-health-attention-list">{attention.map((item, index) => <article key={`${item.subsystem}-${index}`} className={`tone-${tone(item.state)}`}><div className="state"><span>{item.label}</span><strong>{stateLabel(item.state)}</strong></div><p>{item.message}</p>{item.action ? <button type="button" className="ffs-button warning" disabled={Boolean(busyAction)} onClick={() => void recover(item.action!)}><Wrench size={14} /> {busyAction === item.action.id ? 'Working…' : item.action.label}</button> : null}</article>)}</div>}
      </section>

      <section className="ffs-health-summary-grid">
        <Summary label="Required systems" value={String(systems.filter((item) => item.required).length)} detail={`${systems.filter((item) => item.required && item.state === 'healthy').length} healthy`} />
        <Summary label="Optional systems" value={String(systems.filter((item) => !item.required).length)} detail={`${systems.filter((item) => !item.required && item.state === 'disabled').length} disabled`} />
        <Summary label="Stream setup" value={readinessLabel(snapshot?.streamSetup.readiness)} detail={styleLabel(snapshot?.streamSetup.selectedStyle ?? '')} />
        <Summary label="Operating mode" value={mode} detail={`${snapshot?.streamSetup.modes.suppressedFeatures.length ?? 0} suppressed features`} />
      </section>

      <section className="ffs-workspace-card ffs-health-systems-card">
        <header><div><small>SUBSYSTEMS</small><h3>System status</h3></div><ShieldCheck size={19} /></header>
        <div className="ffs-health-system-list">{systems.map((system) => <article key={system.id} className={`tone-${tone(system.state)}`}><div className="ffs-health-system-main"><span className={`indicator ${tone(system.state)}`} /><div><strong>{system.label}</strong><small>{system.required ? 'Required' : 'Optional'} · {stateLabel(system.state)}</small></div></div><p>{system.detail}</p>{system.lastError && system.state !== 'healthy' ? <div className="error">{system.lastError}</div> : null}{system.recoveryAction ? <button type="button" className="ffs-button secondary" disabled={Boolean(busyAction)} onClick={() => void recover(system.recoveryAction!)}><RotateCcw size={13} /> {busyAction === system.recoveryAction.id ? 'Working…' : system.recoveryAction.label}</button> : <span className="no-action">No operator action required</span>}</article>)}</div>
      </section>

      <section className="ffs-workspace-card ffs-health-incidents-card">
        <header>
          <div><small>RECENT HISTORY</small><h3>Incidents &amp; recoveries</h3></div>
          <div className="ffs-health-incidents-heading">
            <History size={18} />
            <strong>{visibleIncidents.length.toLocaleString('en-GB')}</strong>
            <label>
              <span>Show</span>
              <select value={incidentFilter} onChange={(event) => setIncidentFilter(event.target.value as typeof incidentFilter)}>
                <option value="all">All</option>
                <option value="open">Open</option>
                <option value="resolved">Resolved</option>
              </select>
            </label>
          </div>
        </header>
        {incidents.length === 0 ? <div className="ffs-empty-state">No recent operational incidents.</div> : visibleIncidents.length === 0 ? <div className="ffs-empty-state">No incidents match the selected history filter.</div> : <div className="ffs-health-incident-list">{visibleIncidents.slice(0, 20).map((incident) => <article className={incident.resolved_at ? 'resolved' : 'open'} key={incident.id}><div><strong>{incident.summary || friendlyToken(incident.subsystem)}</strong><span>{friendlyToken(incident.subsystem)} · {friendlyToken(incident.severity)} · first seen {formatStamp(incident.first_seen_at)}</span></div><div className="meta"><strong>{incident.resolved_at ? 'Resolved' : stateLabel(incident.state as HealthState)}</strong><small>{incident.resolved_at ? `Resolved ${formatStamp(incident.resolved_at)}` : `Last seen ${formatStamp(incident.last_seen_at)}`}{incident.occurrence_count > 1 ? ` · ${incident.occurrence_count.toLocaleString('en-GB')} occurrences` : ''}</small></div></article>)}</div>}
      </section>

      <section className="ffs-health-context-grid">
        <article className="ffs-workspace-card"><header><div><small>ALERT DELIVERY</small><h3>Current queue</h3></div></header><div className="ffs-health-context-values"><span><strong>{snapshot?.streamSetup.alerts.queued ?? 0}</strong> queued</span><span><strong>{snapshot?.streamSetup.alerts.playing ?? 0}</strong> playing</span><span><strong>{snapshot?.streamSetup.alerts.failed ?? 0}</strong> failed</span></div></article>
        <article className="ffs-workspace-card"><header><div><small>WHATSAMUSIC</small><h3>Current state</h3></div></header><div className="ffs-health-context-text"><strong>{friendlyToken(snapshot?.streamSetup.wam.status ?? 'unknown')}</strong><span>{snapshot?.streamSetup.wam.current?.category ?? 'No category currently reported'}</span></div></article>
      </section>

      <section className="ffs-workspace-card ffs-health-modules-card">
        <header><div><small>REGISTERED CAPABILITIES</small><h3>Modules</h3></div><div className="ffs-health-module-actions"><strong>{modules.length.toLocaleString('en-GB')}</strong>{role === 'owner' ? <a href="#/modules">Detailed diagnostics</a> : null}</div></header>
        {snapshot?.moduleCatalogError ? <div className="ffs-health-module-warning">{snapshot.moduleCatalogError}</div> : null}
        {modules.length === 0 && !snapshot?.moduleCatalogError ? <div className="ffs-empty-state">No registered module metadata was returned.</div> : <div className="ffs-health-module-grid">{modules.map((module) => <article key={module.id}><div><strong>{module.label}</strong><span>{friendlyToken(module.healthState)}</span></div><p>{module.description}</p></article>)}</div>}
      </section>

      <section className="ffs-workspace-card ffs-health-operations-card">
        <header><div><small>DEPLOYMENT &amp; RECOVERY</small><h3>Host maintenance state</h3><p>Read-only production, backup and rollback information.</p></div><span className={`ffs-status ${operationsTone}`}>{operationsLabel}</span></header>
        {snapshot?.operationsError ? <div className="ffs-health-operation-warning">{snapshot.operationsError}</div> : !operations?.available ? <div className="ffs-empty-state">The host maintenance status record has not been initialised yet.</div> : (
          <div className="ffs-health-operations-grid">
            <article><GitCommitHorizontal size={17} /><div><span>Production release</span><strong>{operations.release?.version ?? 'Unknown'}</strong><small>{operations.release?.branch ?? '—'} · <code>{shortCommit(operations.release?.commit)}</code> · {operations.release?.clean === true ? 'clean' : operations.release?.clean === false ? 'changes present' : 'tree unknown'}</small></div></article>
            <article><DatabaseBackup size={17} /><div><span>Latest verified backup</span><strong>{formatStamp(operations.backup?.lastSuccessfulAt)}</strong><small>{operations.backup?.verified === true ? 'checksums + database + sounds verified' : 'verification unavailable'}{operations.backup?.reason ? ` · ${operations.backup.reason}` : ''}</small></div></article>
            <article><ShieldCheck size={17} /><div><span>Last deployment</span><strong>{deploymentLabel(operations)}</strong><small>{operations.deployment?.toVersion ?? 'No recorded target'} · {formatStamp(operations.deployment?.completedAt)}</small></div></article>
            <article><RotateCcw size={17} /><div><span>Recovery point</span><strong>{operations.recovery?.ready ? 'READY' : 'UNVERIFIED'}</strong><small>{operations.recovery?.rollbackVersion ?? 'No rollback release recorded'}{operations.recovery?.rollbackCommit ? ` · ${shortCommit(operations.recovery.rollbackCommit)}` : ''}{operations.recovery?.backupAt ? ` · backup ${formatStamp(operations.recovery.backupAt)}` : ''}</small></div></article>
          </div>
        )}
      </section>
    </div>
  );
}

function Summary({ label, value, detail }: { label: string; value: string; detail: string }) {
  return <article><span>{label}</span><strong>{value}</strong><small>{detail}</small></article>;
}
